import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  Output,
} from '@angular/core';

@Directive({
  selector: '[appModalDialog]',
})
export class CxModalDialogDirective implements AfterViewInit, OnDestroy {
  private static openDialogCount = 0;
  private previouslyFocusedElement: HTMLElement | null = null;

  @HostBinding('attr.tabindex') readonly tabIndex = '-1';
  @Output() appModalDialogDismiss = new EventEmitter<void>();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngAfterViewInit(): void {
    const activeElement = this.document.activeElement as HTMLElement | null;
    this.previouslyFocusedElement = activeElement || null;
    CxModalDialogDirective.openDialogCount++;
    this.document.body.classList.add('cx-modal-dialog-open');

    window.setTimeout(() => this.focusFirstElement());
  }

  ngOnDestroy(): void {
    CxModalDialogDirective.openDialogCount = Math.max(
      0,
      CxModalDialogDirective.openDialogCount - 1
    );
    if (CxModalDialogDirective.openDialogCount === 0) {
      this.document.body.classList.remove('cx-modal-dialog-open');
    }

    this.previouslyFocusedElement?.focus?.();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.appModalDialogDismiss.emit();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.elementRef.nativeElement.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = this.document.activeElement as HTMLElement | null;

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  private focusFirstElement(): void {
    const firstElement = this.getFocusableElements()[0];
    (firstElement || this.elementRef.nativeElement).focus();
  }

  private getFocusableElements(): HTMLElement[] {
    return Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));
  }
}
